(() => {
  'use strict';

  const STORAGE_KEY = 'llb-site-appearance';
  const MODES = ['system', 'light', 'dark'];
  const LABELS = { system: 'System', light: 'Light', dark: 'Dark' };
  const BRAND_NAMES = [
    'Louis Vuitton', 'Saint Laurent', 'Salvatore Ferragamo', 'Bottega Veneta',
    'Balenciaga', 'Burberry', 'Bvlgari', 'Celine', 'Chanel', 'Christian Dior',
    'Dior', 'Fendi', 'Givenchy', 'Goyard', 'Gucci', 'Hermes', 'Hermès', 'Loewe',
    'MCM', 'Miu Miu', 'Prada', 'Valentino', 'Versace', 'Coach'
  ];
  const darkMedia = window.matchMedia('(prefers-color-scheme: dark)');
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const heroPreloadPromises = new Map();
  const heroPreloadImages = [];
  let observerQueued = false;

  document.documentElement.dataset.llbPageHandle = decodeURIComponent(window.location.pathname)
    .split('/')
    .filter(Boolean)
    .pop() || 'home';

  function storedMode() {
    try {
      const value = localStorage.getItem(STORAGE_KEY);
      return MODES.includes(value) ? value : 'system';
    } catch (_) {
      return 'system';
    }
  }

  function resolvedMode(mode) {
    return mode === 'system' ? (darkMedia.matches ? 'dark' : 'light') : mode;
  }

  function extractCssUrl(value) {
    const match = String(value || '').match(/url\((['"]?)(.*?)\1\)/i);
    return match?.[2] || '';
  }

  function heroImageUrls(mode) {
    const heroImages = Array.from(document.querySelectorAll('.lux-editorial-card__image')).slice(0, 3);
    if (!heroImages.length) return [];
    if (mode === 'light') {
      const rootStyle = getComputedStyle(document.documentElement);
      return [1, 2, 3]
        .map((index) => extractCssUrl(rootStyle.getPropertyValue(`--llb-light-hero-${index}-image`)))
        .filter(Boolean);
    }
    return heroImages
      .map((image) => extractCssUrl(image.style.backgroundImage))
      .filter(Boolean);
  }

  function preloadHeroAssets(mode, limit = 3) {
    if (!['light', 'dark'].includes(mode)) return Promise.resolve();
    const boundedLimit = Math.max(1, Math.min(3, limit));
    const cacheKey = `${mode}:${boundedLimit}`;
    if (heroPreloadPromises.has(cacheKey)) return heroPreloadPromises.get(cacheKey);
    const urls = [...new Set(heroImageUrls(mode))];
    if (!urls.length) return Promise.resolve();

    const promise = Promise.all(urls.slice(0, boundedLimit).map((url, index) => new Promise((resolve) => {
      const image = new Image();
      heroPreloadImages.push(image);
      let settled = false;
      const finish = (loaded = false) => {
        if (settled) return;
        settled = true;
        window.clearTimeout(timeoutId);
        resolve(loaded);
      };
      const decode = () => {
        if (typeof image.decode !== 'function') {
          finish(image.naturalWidth > 0);
          return;
        }
        image.decode()
          .then(() => finish(true))
          .catch(() => finish(image.naturalWidth > 0));
      };
      const timeoutId = window.setTimeout(() => finish(false), 8000);
      image.decoding = 'async';
      image.fetchPriority = index === 0 ? 'high' : 'auto';
      image.onload = decode;
      image.onerror = () => finish(false);
      image.src = url;
      if (image.complete && image.naturalWidth) decode();
    }))).then((results) => {
      const ready = results.every(Boolean);
      if (!ready) heroPreloadPromises.delete(cacheKey);
      return ready;
    });

    heroPreloadPromises.set(cacheKey, promise);
    return promise;
  }

  function preloadInitialHero(attempt = 0) {
    const initialResolved = document.documentElement.dataset.llbResolved || resolvedMode(storedMode());
    if (!heroImageUrls(initialResolved).length) {
      if (attempt < 20) window.setTimeout(() => preloadInitialHero(attempt + 1), 100);
      return;
    }
    const initialHeroLimit = window.matchMedia('(max-width: 768px)').matches ? 1 : 3;
    preloadHeroAssets(initialResolved, initialHeroLimit).then((heroReady) => {
      if (heroReady && document.documentElement.dataset.llbResolved === initialResolved) {
        document.documentElement.dataset.llbHeroReady = initialResolved;
      }
    });
  }

  function updateThemeColor(resolved) {
    let meta = document.querySelector('meta[name="theme-color"]');
    if (!meta) {
      meta = document.createElement('meta');
      meta.name = 'theme-color';
      document.head.appendChild(meta);
    }
    meta.content = resolved === 'dark' ? '#000000' : '#efebe4';
  }

  function applyViewportBackground(resolved) {
    const color = resolved === 'dark' ? '#000000' : '#efebe4';
    document.documentElement.style.setProperty('background', color, 'important');
    document.documentElement.style.setProperty('background-color', color, 'important');
    if (document.body) {
      document.body.style.setProperty('background', color, 'important');
      document.body.style.setProperty('background-color', color, 'important');
    }
  }

  function setMode(mode, persist = true) {
    const next = MODES.includes(mode) ? mode : 'system';
    const resolved = resolvedMode(next);
    document.documentElement.dataset.llbAppearance = next;
    document.documentElement.dataset.llbResolved = resolved;
    if (persist) {
      try { localStorage.setItem(STORAGE_KEY, next); } catch (_) {}
    }
    document.querySelectorAll('.llb-appearance-picker__button').forEach((button) => {
      button.setAttribute('aria-pressed', String(button.dataset.mode === next));
      button.setAttribute('aria-checked', String(button.dataset.mode === next));
    });
    const trigger = document.querySelector('.llb-appearance-picker__trigger');
    if (trigger) {
      trigger.setAttribute('aria-label', `Appearance: ${LABELS[next]}`);
      trigger.setAttribute('title', `Appearance: ${LABELS[next]}`);
      trigger.dataset.mode = next;
    }
    applyViewportBackground(resolved);
    updateThemeColor(resolved);
  }

  function makePicker() {
    if (document.querySelector('.llb-appearance-picker')) return;
    const picker = document.createElement('div');
    picker.className = 'llb-appearance-picker';
    const trigger = document.createElement('button');
    trigger.type = 'button';
    trigger.className = 'llb-appearance-picker__trigger';
    trigger.setAttribute('aria-haspopup', 'menu');
    trigger.setAttribute('aria-expanded', 'false');
    trigger.innerHTML = '<svg viewBox="0 0 20 20" aria-hidden="true"><circle cx="10" cy="10" r="3.25" fill="none" stroke="currentColor" stroke-width="1.35"/><path d="M10 2v1.4M10 16.6V18M2 10h1.4M16.6 10H18M4.34 4.34l1 1M14.66 14.66l1 1M15.66 4.34l-1 1M5.34 14.66l-1 1" fill="none" stroke="currentColor" stroke-width="1.35" stroke-linecap="round"/></svg>';
    const panel = document.createElement('div');
    panel.className = 'llb-appearance-picker__panel';
    panel.setAttribute('role', 'menu');
    panel.setAttribute('aria-label', 'Appearance');
    panel.hidden = true;
    MODES.forEach((mode) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'llb-appearance-picker__button';
      button.dataset.mode = mode;
      button.textContent = LABELS[mode];
      button.setAttribute('role', 'menuitemradio');
      button.addEventListener('click', async () => {
        if (trigger.dataset.loading === 'true') return;
        trigger.dataset.loading = 'true';
        trigger.setAttribute('aria-busy', 'true');
        button.setAttribute('aria-busy', 'true');
        const targetResolved = resolvedMode(mode);
        const heroReady = await preloadHeroAssets(targetResolved);
        setMode(mode);
        if (heroReady) document.documentElement.dataset.llbHeroReady = targetResolved;
        else delete document.documentElement.dataset.llbHeroReady;
        panel.hidden = true;
        trigger.setAttribute('aria-expanded', 'false');
        trigger.removeAttribute('aria-busy');
        button.removeAttribute('aria-busy');
        delete trigger.dataset.loading;
        trigger.focus({ preventScroll: true });
      });
      panel.appendChild(button);
    });
    trigger.addEventListener('click', () => {
      const willOpen = panel.hidden;
      panel.hidden = !willOpen;
      trigger.setAttribute('aria-expanded', String(willOpen));
      if (willOpen) {
        const current = document.documentElement.dataset.llbResolved || resolvedMode(storedMode());
        preloadHeroAssets(current === 'light' ? 'dark' : 'light');
        panel.querySelector('[aria-checked="true"]')?.focus({ preventScroll: true });
      }
    });
    picker.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') return;
      panel.hidden = true;
      trigger.setAttribute('aria-expanded', 'false');
      trigger.focus({ preventScroll: true });
    });
    document.addEventListener('pointerdown', (event) => {
      if (picker.contains(event.target)) return;
      panel.hidden = true;
      trigger.setAttribute('aria-expanded', 'false');
    });
    picker.append(trigger, panel);
    const headerAnchor = document.querySelector('.site-nav__icons, .header-item--icons');
    if (headerAnchor) headerAnchor.insertBefore(picker, headerAnchor.firstChild);
    else document.querySelector('.site-footer, footer')?.prepend(picker);
    setMode(storedMode(), false);
    window.requestAnimationFrame(() => preloadInitialHero());
  }

  function forceStyle(element, property, value) {
    if (!element) return;
    element.style.setProperty(property, value, 'important');
  }

  function enforceThemeSurfaces(root = document) {
    const one = (selector) => root.querySelector(selector);
    const all = (selector) => root.querySelectorAll(selector);

    forceStyle(document.body, 'background', 'var(--llb-canvas)');
    forceStyle(one('#MainContent, #main-content'), 'background', 'var(--llb-canvas)');
    forceStyle(one('.page-container'), 'background', 'var(--llb-canvas)');

    all('.header-wrapper, .site-header').forEach((element) => {
      forceStyle(element, 'background', 'var(--llb-header)');
      forceStyle(element, 'color', 'var(--llb-text)');
      forceStyle(element, 'border-color', 'var(--llb-separator)');
    });
    all('.header-wrapper .site-nav__link, .header-wrapper .site-nav__link--icon, .header-wrapper button, .header-wrapper svg').forEach((element) => {
      forceStyle(element, 'color', 'var(--llb-text)');
    });

    all('.template-index .lux-featured-drop, .template-index .lux-curated-tiles, .template-index .llb-cr').forEach((element) => {
      forceStyle(element, 'background-color', 'var(--llb-section-bg, var(--llb-canvas))');
      forceStyle(element, 'color', 'var(--llb-text)');
    });
    all('.template-index .lux-featured-drop__title, .template-index .lux-curated-tiles__title, .template-index .lux-doran-video__title, .template-index .llb-cr__title, .template-index .lux-trust-atelier__title, .template-index .lux-trust-atelier__copy').forEach((element) => {
      forceStyle(element, 'color', 'var(--llb-text)');
      forceStyle(element, 'opacity', '1');
      forceStyle(element, 'transform', 'none');
      forceStyle(element, 'text-shadow', 'none');
    });
    all('.template-index .lux-featured-drop .lux-kicker, .template-index .lux-featured-drop .eyebrow, .template-index .lux-curated-tiles .lux-kicker, .template-index .lux-curated-tiles .eyebrow, .template-index .lux-doran-video .lux-kicker, .template-index .lux-doran-video .eyebrow, .template-index .lux-trust-atelier .lux-kicker, .template-index .lux-trust-atelier .eyebrow, .template-index .llb-cr .lux-kicker, .template-index .llb-cr .eyebrow').forEach((element) => {
      forceStyle(element, 'color', 'var(--llb-caption)');
      forceStyle(element, 'opacity', '1');
      forceStyle(element, 'text-shadow', 'none');
    });
    all('.template-index .lux-featured-drop .lux-copy, .template-index .lux-featured-drop__header-aside p, .template-index .lux-curated-tiles .lux-copy, .template-index .lux-curated-tiles__header-aside p, .template-index .lux-doran-video .lux-copy, .template-index .lux-trust-atelier .lux-copy, .template-index .llb-cr .lux-copy').forEach((element) => {
      forceStyle(element, 'color', 'var(--llb-text-2)');
      forceStyle(element, 'opacity', '1');
      forceStyle(element, 'text-shadow', 'none');
    });
    all('.template-index .lux-featured-drop__view-all, .template-index .lux-curated-tiles__view-all').forEach((element) => {
      forceStyle(element, 'color', 'var(--llb-text)');
      forceStyle(element, 'opacity', '1');
      forceStyle(element, 'text-shadow', 'none');
    });

    all('.grid-product .grid-product__title, .grid-product .grid-product__title--body, .grid-product .grid-product__title *, .grid-product .grid-product__title--body *').forEach((element) => {
      forceStyle(element, 'color', 'var(--llb-text)');
      forceStyle(element, 'opacity', '1');
      forceStyle(element, 'text-shadow', 'none');
    });
    all('.grid-product .grid-product__price, .grid-product .grid-product__price *').forEach((element) => {
      forceStyle(element, 'color', 'var(--llb-functional-gold)');
      forceStyle(element, 'opacity', '1');
      forceStyle(element, 'text-shadow', 'none');
    });
    all('.grid-product .llb-apple-card__brand').forEach((element) => {
      forceStyle(element, 'color', 'var(--llb-caption)');
    });
    all('.grid-product .llb-apple-card__assurance').forEach((element) => {
      forceStyle(element, 'color', 'var(--llb-success)');
    });

    all('.template-page #MainContent > .shopify-section, .template-page #main-content > .shopify-section, .template-search #MainContent > .shopify-section, .template-cart #MainContent > .shopify-section').forEach((element) => {
      forceStyle(element, 'background-color', 'var(--llb-canvas)');
      forceStyle(element, 'color', 'var(--llb-text)');
    });
    all('.template-page [id*="__form"], .template-page [id*="__form"] > .index-section').forEach((element) => {
      forceStyle(element, 'background-color', 'var(--llb-canvas)');
      forceStyle(element, 'color', 'var(--llb-text)');
    });

    all('.template-collection #MainContent > .shopify-section, .template-collection #main-content > .shopify-section, .template-collection .lux-collection, .template-collection .collection-content, .template-collection .collection-grid__wrapper, .template-collection .page-width, .template-collection .page-content').forEach((element) => {
      forceStyle(element, 'background-color', 'var(--llb-canvas)');
      forceStyle(element, 'color', 'var(--llb-text)');
    });
    all('.template-collection .lux-collection__afterword, .template-collection .lux-collection-geo').forEach((element) => {
      forceStyle(element, 'background-color', 'var(--llb-section-panel, var(--llb-canvas))');
      forceStyle(element, 'color', 'var(--llb-text)');
    });
    all('.template-collection .lux-collection__afterword h2, .template-collection .lux-collection__afterword h3, .template-collection .lux-collection__afterword p, .template-collection .lux-collection__afterword li, .template-collection .lux-collection__afterword summary, .template-collection .lux-collection__afterword .collapsible-trigger, .template-collection .lux-collection-geo h2, .template-collection .lux-collection-geo h3, .template-collection .lux-collection-geo p, .template-collection .lux-collection-geo li, .template-collection .lux-collection-geo summary').forEach((element) => {
      forceStyle(element, 'color', 'var(--llb-text)');
    });

    all('.lux-faq, .lux-contact-form, [id*="faq_h6KcT9"], [id*="contact_form_tA9qhb"], [id*="contact_form_tA9qhb"] > .index-section').forEach((element) => {
      forceStyle(element, 'background-color', 'var(--llb-canvas)');
      forceStyle(element, 'color', 'var(--llb-text)');
    });

    all('.product-single__meta').forEach((element) => {
      forceStyle(element, 'background', 'var(--llb-surface-solid)');
      forceStyle(element, 'color', 'var(--llb-text)');
      forceStyle(element, 'border-color', 'var(--llb-separator)');
    });
    all('.product-single__meta .product-single__title, .product-single__meta .product__price, .product-single__meta .product-single__vendor, .product-single__meta .collapsible-trigger, .product-single__meta .collapsible-content, .product-single__meta .rte, .product-single__meta p, .product-single__meta label').forEach((element) => {
      forceStyle(element, 'color', 'var(--llb-text)');
    });
    all('.product-single__meta .lux-product-condition h2, .product-single__meta .lux-product-condition h3, .product-single__meta .lux-product-condition p, .product-single__meta .lux-product-condition li, .product-single__meta .lux-product-assurance h2, .product-single__meta .lux-product-assurance h3, .product-single__meta .lux-product-assurance p, .product-single__meta .lux-product-assurance li').forEach((element) => {
      forceStyle(element, 'color', 'var(--llb-text)');
    });
    all('.product-single__meta .lux-product-condition__head strong').forEach((element) => {
      forceStyle(element, 'color', 'var(--llb-functional-gold)');
    });
    all('.product-single__meta .lux-product-assurance__badges span').forEach((element) => {
      forceStyle(element, 'color', 'var(--llb-text-2)');
    });
    all('.product-single__meta .lux-product-assurance__badges span:first-child').forEach((element) => {
      forceStyle(element, 'color', 'var(--llb-success)');
    });

    all('.lux-designer-directory__title, .lux-designer-directory__text, .lux-designer-card, .lux-wholesale-portal__title, .lux-wholesale-portal__text').forEach((element) => {
      forceStyle(element, 'opacity', '1');
      forceStyle(element, 'transform', 'none');
      forceStyle(element, 'color', 'var(--llb-text)');
    });
    all('.lux-designer-directory__grid').forEach((element) => {
      forceStyle(element, 'gap', 'clamp(8px, 1vw, 14px)');
    });
    all('.lux-designer-card, .lux-designer-card__logo, .collection-image--brand-logo').forEach((element) => {
      forceStyle(element, 'background', 'var(--llb-surface-solid)');
      forceStyle(element, 'border-color', 'var(--llb-separator)');
    });
    all('.lux-faq h2, .lux-faq summary, .lux-faq .collapsible-trigger, [id*="faq_h6KcT9"] h2, [id*="faq_h6KcT9"] .collapsible-trigger, .lux-contact-form h2, .lux-contact-form label, [id*="contact_form_tA9qhb"] h2, [id*="contact_form_tA9qhb"] label').forEach((element) => {
      forceStyle(element, 'color', 'var(--llb-text)');
    });
    all('.template-page [id*="__form"] h1, .template-page [id*="__form"] h2, .template-page [id*="__form"] p, .template-page [id*="__form"] label').forEach((element) => {
      forceStyle(element, 'color', 'var(--llb-text)');
    });
  }

  function repairBrandMarks(root = document) {
    root.querySelectorAll(
      '.lux-curated-tiles--logos .lux-brand-mark, .lux-designer-directory .lux-brand-mark',
    ).forEach((mark) => {
      const decorativeLayer = mark.closest(
        '.lux-curated-tile, [data-lux-brand-card]',
      )?.querySelector('.lux-curated-tile__image');
      if (decorativeLayer) {
        forceStyle(decorativeLayer, 'display', 'none');
        forceStyle(decorativeLayer, 'visibility', 'hidden');
        forceStyle(decorativeLayer, 'opacity', '0');
        decorativeLayer.setAttribute('aria-hidden', 'true');
      }

      const asset = mark.querySelector('.lux-brand-mark__asset, img');
      const fallback = mark.querySelector('.lux-brand-mark__fallback');
      const inlineLogo = mark.querySelector(':scope > svg');
      const isGucci = Boolean(mark.closest('.lux-brand-logo--gucci'));
      if (isGucci && asset) {
        forceStyle(mark, 'width', 'clamp(96px, 12vw, 174px)');
        forceStyle(mark, 'max-width', '174px');
        forceStyle(mark, 'height', 'clamp(26px, 3.2vw, 46px)');
        forceStyle(mark, 'min-height', '0');
        forceStyle(mark, 'max-height', '46px');
        forceStyle(mark, 'box-sizing', 'border-box');
        forceStyle(mark, 'padding', '0');
        forceStyle(mark, 'line-height', '0');
        forceStyle(mark, 'place-items', 'start center');
        forceStyle(mark, 'overflow', 'hidden');
        forceStyle(asset, 'width', '100%');
        forceStyle(asset, 'height', 'auto');
        forceStyle(asset, 'max-width', '100%');
        forceStyle(asset, 'max-height', 'none');
        forceStyle(asset, 'object-fit', 'contain');
        forceStyle(asset, 'object-position', 'top center');
        forceStyle(asset, 'transform', 'none');
        forceStyle(asset, 'transform-origin', 'top center');
      }
      if (!asset || !fallback) {
        if (inlineLogo) {
          forceStyle(inlineLogo, 'display', 'block');
          forceStyle(inlineLogo, 'visibility', 'visible');
          forceStyle(inlineLogo, 'opacity', '1');
          mark.dataset.llbBrandRepresentation = 'inline';
        }
        return;
      }

      const showAsset = () => {
        forceStyle(asset, 'display', 'block');
        forceStyle(asset, 'visibility', 'visible');
        forceStyle(asset, 'opacity', '1');
        forceStyle(fallback, 'display', 'none');
        mark.dataset.llbBrandRepresentation = 'asset';
      };
      const showFallback = () => {
        forceStyle(asset, 'display', 'none');
        forceStyle(fallback, 'display', 'block');
        forceStyle(fallback, 'visibility', 'visible');
        forceStyle(fallback, 'opacity', '1');
        mark.dataset.llbBrandRepresentation = 'fallback';
      };

      if (asset.tagName === 'IMG') {
        if (asset.dataset.llbBrandRepairBound !== 'true') {
          asset.dataset.llbBrandRepairBound = 'true';
          asset.addEventListener('load', showAsset);
          asset.addEventListener('error', showFallback);
        }
        if (asset.complete && asset.naturalWidth < 1) showFallback();
        else showAsset();
        return;
      }
      showAsset();
    });
  }

  function hydrateCardImage(image) {
    if (!image) return;
    const template = image.getAttribute('data-lux-card-src') || image.getAttribute('data-src');
    if (!template) return;
    const targetWidth = window.devicePixelRatio > 1 ? 720 : 540;
    const source = template.replace('{width}', String(targetWidth));
    if (!image.getAttribute('src') || image.naturalWidth <= 1) image.setAttribute('src', source);
    if (!image.getAttribute('srcset')) {
      image.setAttribute('srcset', [360, 540, 720, 900]
        .map((width) => `${template.replace('{width}', String(width))} ${width}w`)
        .join(', '));
    }
    image.setAttribute('sizes', image.getAttribute('sizes') || '(min-width: 769px) 25vw, 50vw');
    image.classList.add('lazyloaded');
  }

  function enhanceHoverGalleries(root = document) {
    if (!window.matchMedia('(min-width: 769px) and (hover: hover) and (pointer: fine)').matches) return;

    root.querySelectorAll('.lux-product-card').forEach((card) => {
      if (card.dataset.llbHoverGalleryBound === 'true') return;
      const viewport = card.querySelector('.lux-card-slider__viewport, [data-lux-card-viewport]');
      const track = card.querySelector('.lux-card-slider__track, [data-lux-card-track]');
      const slides = card.querySelectorAll('.lux-card-slider__slide, [data-lux-card-slide]');
      if (!viewport || !track || slides.length < 2) return;

      card.dataset.llbHoverGalleryBound = 'true';
      card.setAttribute('data-lux-has-multiple', 'true');
      let hoverSession = 0;
      let loadTimer = 0;
      let hovering = false;
      let preview = card.querySelector('.llb-hover-gallery-preview');

      if (!preview) {
        preview = document.createElement('img');
        preview.className = 'llb-hover-gallery-preview';
        preview.alt = '';
        preview.decoding = 'async';
        preview.loading = 'eager';
        preview.fetchPriority = 'high';
        preview.setAttribute('aria-hidden', 'true');
        viewport.appendChild(preview);
      }

      const deactivate = () => {
        hovering = false;
        hoverSession += 1;
        const departureSession = hoverSession;
        window.clearTimeout(loadTimer);
        track.style.setProperty('--lux-card-offset', '0%', 'important');
        card.setAttribute('data-lux-active-index', '0');
        card.setAttribute('data-llb-hover-index', '0');
        window.requestAnimationFrame(() => {
          if (departureSession !== hoverSession || hovering) return;
          card.classList.remove('llb-hover-gallery-active');
          card.removeAttribute('data-llb-hover-pending');
          window.requestAnimationFrame(() => {
            if (departureSession !== hoverSession || hovering) return;
            track.style.removeProperty('--lux-card-offset');
          });
        });
      };
      const activate = (session) => {
        if (session !== hoverSession || !hovering) return;
        if (!preview.complete || preview.naturalWidth < 2) return;
        card.classList.add('llb-hover-gallery-active');
        card.setAttribute('data-llb-hover-index', '1');
      };
      const previewSecondImage = () => {
        hovering = true;
        hoverSession += 1;
        card.setAttribute('data-llb-hover-pending', 'true');
        const session = hoverSession;
        window.clearTimeout(loadTimer);
        const image = slides[1].querySelector('img');
        hydrateCardImage(image);
        const template = image?.getAttribute('data-lux-card-src') || image?.getAttribute('data-src') || '';
        const source = image?.currentSrc && image.naturalWidth > 1
          ? image.currentSrc
          : template.replace('{width}', window.devicePixelRatio > 1 ? '720' : '540');
        if (!source) {
          card.removeAttribute('data-llb-hover-pending');
          return;
        }

        if (preview.getAttribute('src') !== source) {
          preview.removeAttribute('srcset');
          preview.src = source;
        }

        const revealDecoded = () => {
          const decoded = typeof preview.decode === 'function' ? preview.decode() : Promise.resolve();
          decoded
            .catch(() => {})
            .then(() => activate(session));
        };
        if (preview.complete && preview.naturalWidth > 1) {
          window.requestAnimationFrame(revealDecoded);
          return;
        }

        const onLoad = () => {
          cleanup();
          revealDecoded();
        };
        const onError = () => {
          cleanup();
          if (session === hoverSession) card.removeAttribute('data-llb-hover-pending');
        };
        const cleanup = () => {
          window.clearTimeout(loadTimer);
          preview.removeEventListener('load', onLoad);
          preview.removeEventListener('error', onError);
        };
        preview.addEventListener('load', onLoad);
        preview.addEventListener('error', onError, { once: true });
        loadTimer = window.setTimeout(cleanup, 3000);
      };

      card.addEventListener('mouseenter', previewSecondImage);
      card.addEventListener('mouseleave', deactivate);
      card.addEventListener('focusout', (event) => {
        if (!card.contains(event.relatedTarget)) deactivate();
      });
    });
  }

  function textOf(element) {
    return (element?.textContent || '').replace(/\s+/g, ' ').trim();
  }

  function findBrand(title) {
    const normalized = title.toLocaleLowerCase();
    return BRAND_NAMES.find((brand) => normalized.includes(brand.toLocaleLowerCase())) || '';
  }

  function cleanTitle(title, brand) {
    let cleaned = title.replace(/^pre[- ]owned\s+/i, '').replace(/\s+/g, ' ').trim();
    if (brand) cleaned = cleaned.replace(new RegExp(`^${brand.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\s+`, 'i'), '');
    cleaned = cleaned.replace(/\s+[A-Z]\d{7,}$/i, '').trim();
    return cleaned || title;
  }

  function photoCount(card) {
    const explicit = card.querySelectorAll('.lux-card-slider__slide, .product__photo-zoom').length;
    if (explicit > 1) return explicit;
    const dots = card.querySelectorAll('.lux-card-slider__dot').length;
    return dots > 1 ? dots : 0;
  }

  function enhanceCard(card) {
    const shell = card.matches('.grid-product__content') ? card : card.querySelector('.grid-product__content');
    if (!shell || shell.dataset.llbAppleEnhanced === 'true') return;
    shell.dataset.llbAppleEnhanced = 'true';

    const title = shell.querySelector('.grid-product__title, .grid-product__title--body');
    const meta = shell.querySelector('.grid-product__meta');
    const originalTitle = textOf(title);
    if (title && meta && originalTitle) {
      const brand = findBrand(originalTitle);
      if (brand && !meta.querySelector('.llb-apple-card__brand')) {
        const eyebrow = document.createElement('span');
        eyebrow.className = 'llb-apple-card__brand';
        eyebrow.textContent = brand;
        meta.insertBefore(eyebrow, title);
        title.textContent = cleanTitle(originalTitle, brand);
        title.setAttribute('title', originalTitle);
      }
      if (!meta.querySelector('.llb-apple-card__assurance')) {
        const assurance = document.createElement('span');
        assurance.className = 'llb-apple-card__assurance';
        assurance.innerHTML = '<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M8 1.3 13.3 3v4.1c0 3.3-2.1 6.3-5.3 7.6-3.2-1.3-5.3-4.3-5.3-7.6V3L8 1.3Z" fill="none" stroke="currentColor" stroke-width="1.45"/><path d="m5.25 8 1.65 1.65 3.75-4" fill="none" stroke="currentColor" stroke-width="1.45" stroke-linecap="round" stroke-linejoin="round"/></svg><span>Authenticated · One of one</span>';
        meta.appendChild(assurance);
      }
    }

    const count = photoCount(shell);
    if (count > 1 && !shell.querySelector('.llb-apple-card__count')) {
      const badge = document.createElement('span');
      badge.className = 'llb-apple-card__count';
      badge.textContent = `${count} photos`;
      shell.appendChild(badge);
    }

    const link = shell.querySelector('a.grid-product__link, a[href*="/products/"]');
    if (link && !shell.querySelector('.llb-apple-card__action')) {
      const action = document.createElement('span');
      action.className = 'llb-apple-card__action';
      action.setAttribute('aria-hidden', 'true');
      action.innerHTML = '<svg viewBox="0 0 16 16"><path d="M3 8h10M9 4l4 4-4 4" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>';
      shell.appendChild(action);
    }

    bindPress(shell);
  }

  function animateScale(element, scale) {
    if (reducedMotion.matches || typeof element.animate !== 'function') return;
    const transform = getComputedStyle(element).transform;
    const from = transform === 'none' ? 'translate3d(0,0,0) scale(1)' : transform;
    element.getAnimations().forEach((animation) => animation.cancel());
    element.animate(
      [{ transform: from }, { transform: `translate3d(0,0,0) scale(${scale})` }],
      { duration: scale < 1 ? 105 : 180, easing: scale < 1 ? 'ease-out' : 'cubic-bezier(.2,.8,.2,1)', fill: 'forwards' }
    );
  }

  function bindPress(shell) {
    if (shell.dataset.llbPressBound === 'true') return;
    shell.dataset.llbPressBound = 'true';
    shell.addEventListener('pointerdown', (event) => {
      if (event.button !== 0) return;
      try { shell.setPointerCapture(event.pointerId); } catch (_) {}
      animateScale(shell, 0.985);
    });
    const release = () => animateScale(shell, 1);
    shell.addEventListener('pointerup', release);
    shell.addEventListener('pointercancel', release);
    shell.addEventListener('lostpointercapture', release);
  }

  function enhanceCards(root = document) {
    root.querySelectorAll('.lux-product-card, .grid-product').forEach(enhanceCard);
  }

  function enhanceDoranPosters(root = document) {
    root.querySelectorAll('.lux-doran-video__frame video[poster]').forEach((video) => {
      const poster = video.currentSrc && video.poster ? video.poster : video.getAttribute('poster');
      if (!poster) return;
      const posterValue = `url(${JSON.stringify(poster)})`;
      const slide = video.closest('.drv-carousel-item-container') || video.parentElement;
      slide?.style.setProperty('--llb-doran-poster', posterValue);
      video.style.setProperty('--llb-doran-poster', posterValue);
      const media = video.parentElement;
      if (media && !media.querySelector(':scope > .llb-doran-poster')) {
        const posterImage = document.createElement('img');
        posterImage.className = 'llb-doran-poster';
        posterImage.src = poster;
        posterImage.alt = '';
        posterImage.loading = 'eager';
        posterImage.decoding = 'async';
        posterImage.fetchPriority = 'high';
        posterImage.setAttribute('aria-hidden', 'true');
        media.appendChild(posterImage);
      }
    });
  }

  function repairWholesaleLinks(root = document) {
    const targets = {
      contact_form_tA9qhb: document.querySelector('[id$="__contact_form_tA9qhb"]'),
      faq_h6KcT9: document.querySelector('[id$="__faq_h6KcT9"]')
    };
    root.querySelectorAll('.lux-wholesale-portal a, [class*="wholesale"] a').forEach((link) => {
      const label = textOf(link).toLocaleLowerCase();
      const href = link.getAttribute('href') || '';
      const targetKey =
        href.includes('contact_form_tA9qhb') || label.includes('request access')
          ? 'contact_form_tA9qhb'
          : href.includes('faq_h6KcT9') || label.includes('view faq')
            ? 'faq_h6KcT9'
            : '';
      const target = targets[targetKey];
      if (target?.id) link.setAttribute('href', `#${target.id}`);
    });
  }

  function scheduleEnhance() {
    if (observerQueued) return;
    observerQueued = true;
    requestAnimationFrame(() => {
      observerQueued = false;
      enforceThemeSurfaces();
      repairBrandMarks();
      enhanceCards();
      enhanceHoverGalleries();
      enhanceDoranPosters();
      repairWholesaleLinks();
    });
  }

  function init() {
    makePicker();
    enforceThemeSurfaces();
    repairBrandMarks();
    enhanceCards();
    enhanceHoverGalleries();
    enhanceDoranPosters();
    repairWholesaleLinks();
    const observer = new MutationObserver(scheduleEnhance);
    observer.observe(document.body, { childList: true, subtree: true });
    document.addEventListener('shopify:section:load', scheduleEnhance);
    document.addEventListener('collection:reloaded', scheduleEnhance);
    window.addEventListener('resize', scheduleEnhance, { passive: true });
    window.addEventListener('orientationchange', scheduleEnhance, { passive: true });
    window.addEventListener('load', () => {
      setMode(storedMode(), false);
      enforceThemeSurfaces();
      repairBrandMarks();
      enhanceHoverGalleries();
      enhanceDoranPosters();
      repairWholesaleLinks();
      preloadInitialHero();
    }, { once: true });
    darkMedia.addEventListener?.('change', () => {
      if (storedMode() === 'system') setMode('system', false);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();

// LLB_THEME_FILES_CHANNEL brand-hover-fix-20260723
